import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useBlocker, type BlockerFunction } from 'react-router';
import { asksBeforeLeaving, holdUnload, namesLine, unsaved } from '../unsaved.ts';
import { LeaveQuestion, UnsavedBar } from './ui.tsx';

/**
 * Knowing what is saved (P-29; design study 28, A), mounted once in App, over every page, as the Saved notice is: the
 * bar at the window's foot for the form changed last (src/unsaved.ts, told by each form's `useUnsaved`), and the
 * question before leaving. Another page inside the app is held by the router (`useBlocker`, which is why main.tsx makes
 * a data router) and asked in the portal's own words; closing or reloading the tab asks with the browser's own.
 */
export function UnsavedChanges() {
  const state = useSyncExternalStore(unsaved.subscribe, unsaved.current, unsaved.current);
  const shouldBlock = useCallback<BlockerFunction>(({ currentLocation, nextLocation }) => asksBeforeLeaving(unsaved.any(), currentLocation, nextLocation), []);
  const blocker = useBlocker(shouldBlock);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    let live = true;
    void unsaved.ask().then((leave) => {
      if (!live) return;
      if (leave) blocker.proceed();
      else blocker.reset();
    });
    return () => {
      live = false;
    };
  }, [blocker]);
  useEffect(() => {
    const hold = (e: BeforeUnloadEvent) => holdUnload(e, unsaved.any());
    window.addEventListener('beforeunload', hold);
    return () => window.removeEventListener('beforeunload', hold);
  }, []);
  const stay = useCallback(() => unsaved.answer(false), []);
  const leave = useCallback(() => unsaved.answer(true), []);
  const s = state.shown;
  return (
    <>
      {s ? (
        <UnsavedBar
          names={namesLine(s.names)}
          lead={s.lead}
          saveWords={s.saveWords}
          busy={s.busy}
          canSave={s.canSave !== false}
          error={s.error}
          onSave={s.save}
          onDiscard={s.discard}
        />
      ) : null}
      {state.asking ? <LeaveQuestion names={namesLine(state.names)} onStay={stay} onLeave={leave} /> : null}
    </>
  );
}
